import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import '../button';
import '../placeholders';

import { AtlSidebarStripeItemProps } from './props';

import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';

import * as TagNames from './sidebar.tag-names';
import { AtlAriaIconProps } from '../icon/icon.props';
import { getGlobalStyleSheets } from '../../global-styles';
import { SidebarItemClickEvent } from './events';

@customElement(TagNames.SidebarStripeItem)
export class AtlSidebarStripeItem
   extends LitElement
   implements AtlSidebarStripeItemProps
{
   declare iconName?: string;
   declare alias?: string;
   declare itemTitle?: string;
   declare active?: boolean;
   declare expandSubItemsOnFirstLoad?: boolean;

   declare _isOpen: boolean;

   static properties = {
      iconName: { attribute: 'icon-name', type: String },
      alias: { type: String },
      itemTitle: { attribute: 'item-title', type: Object },
      active: { type: Boolean },
      expandSubItemsOnFirstLoad: { type: Boolean },
      _isOpen: { type: Boolean, state: true },
   };

   static styles? = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
         }
      `,
   ];

   constructor() {
      super();
      this._isOpen = false;
   }

   get subItems() {
      const slot = this.shadowRoot?.querySelector(
         'slot[name="subitems"]'
      ) as HTMLSlotElement;
      return slot?.assignedElements() || [];
   }

   connectedCallback(): void {
      super.connectedCallback();
      if (this.expandSubItemsOnFirstLoad) {
         this._isOpen = true;
      }
   }

   private _onSubItemsChanged = (e: Event) => {
      const slot = e.target as HTMLSlotElement;

      const assignedElements = slot.assignedElements({ flatten: true });

      const sideBarItems = assignedElements.filter(
         (el) =>
            el instanceof HTMLElement && el.matches(TagNames.SidebarStripeItem)
      ) as HTMLElement[];

      const activeItemsCount = sideBarItems.filter((item) =>
         item.hasAttribute('active')
      ).length;

      if (!this._isOpen && activeItemsCount > 0) {
         this._isOpen = true;
      }

      this.requestUpdate();
   };

   renderSidebarSubItems(): unknown {
      const classNames = {
         'sidebar-subitems': true,
         show: this._isOpen,
      };
      return html`<div class=${classMap(classNames)}>
         <slot name="subitems" @slotchange=${this._onSubItemsChanged}> </slot>
      </div> `;
   }

   renderExpandIcon() {
      if (!this.subItems.length) return nothing;

      return html`<atlantis-icon
         source="FontAwesome"
         class="icon"
         name="chevron-${this._isOpen ? 'up' : 'down'}"
         .iconAriaSettings=${{ ariaHidden: true } as AtlAriaIconProps}
      ></atlantis-icon>`;
   }

   render() {
      const icon = this.iconName
         ? html`<atlantis-icon
              source="FontAwesome"
              class="icon"
              name="${this.iconName}"
              .iconAriaSettings=${{ ariaHidden: true }}
           ></atlantis-icon>`
         : nothing;

      // Check if the parent item is sidebar-stripe-item
      const isSubItem =
         this.parentElement?.tagName.toLowerCase() ===
         TagNames.SidebarStripeItem;

      const classNames = {
         'sidebar-item': !isSubItem,
         'sidebar-subitem': isSubItem,
         active: this.active === true,
      };

      return html`
         <div class=${classMap(classNames)}>
            <button
               type="button"
               class="btn btn-link"
               title="${ifDefined(this.itemTitle)}"
               @click=${this._handleSidebarItemClick}
            >
               ${icon}
               <span class="label">
                  <slot></slot>
               </span>
               ${this.renderExpandIcon()}
            </button>
            ${this.renderSidebarSubItems()}
         </div>
      `;
   }

   private _handleSidebarItemClick = () => {
      if (this.subItems.length > 0) {
         this._isOpen = !this._isOpen;
         return;
      }
      this.dispatchEvent(new SidebarItemClickEvent());
   };
}
